import jQuery from 'jquery';
import { ZIDX } from "./standaloneLibrary.js";
import { ZIDXBase } from "./base.js";

let $ = jQuery.noConflict();

export interface LocationTag {
    field: string;   // "City", "postalCode", "County", "Subdivision", "Address"
    value: string;
}

interface SlotElements {
    slotId: string;
    $container: JQuery;
    $pillbox: JQuery;
    $input: JQuery;
    $tagResults: JQuery;
    $addrInput: JQuery;
    $addrSuggestions: JQuery;
    $radiusSelect: JQuery;
    $featuredCities: JQuery;
    $tabLocation: JQuery;
    $tabNearLocation: JQuery;
    $panelLocation: JQuery;
    $panelNearLocation: JQuery;
}

/**
 * Manages the Location mega-menu in the search bar AND in the More Filters panel.
 * Both slots share the same state (locations[], activeTab, selectedPlace).
 * Any user action in either slot updates state and re-renders both.
 */
export class LocationMegaMenu {
    private zidx: ZIDX;
    private base: ZIDXBase;
    private root: any;

    locations: LocationTag[] = [];
    activeTab: string = "location";
    selectedPlace: { formattedAddress: string; lat: number; lng: number } | null = null;

    private placesSessionId: string | null = null;
    private currentTagsController: AbortController | null = null;
    private addrSuggestionTimeout: any = 0;
    private addressSuggestionsVisible = false;

    private slots: SlotElements[] = [];
    private featuredCities: string[] = [];

    constructor(zidx: ZIDX, base: ZIDXBase) {
        this.zidx = zidx;
        this.base = base;
        this.root = zidx.peakidxShadowRoot;
    }

    init() {
        // Detach old slot event handlers by clearing references.
        // The old DOM nodes are already gone after an SPA transition,
        // so we just need to drop our stale references.
        this.slots = [];

        // Re-read featured cities from the current DOM
        const citiesAttr = $("#zidxFeaturedCities", this.root).attr("data-cities") || "";
        this.featuredCities = citiesAttr ? citiesAttr.split("~") : [];

        // Primary slot: the popup in the top search bar (absent in disableAdvancedSearch mode)
        const $primaryContainer = $("#zidxSearchLocationContainer .zidxPopupLocation", this.root);
        if ($primaryContainer.length) {
            this.initSlot("primary", $primaryContainer);
        }

        // More-filters slot: rendered into the expandable Location section
        const $moreSlot = $("#zidxMoreLocationSlot", this.root);
        if ($moreSlot.length) {
            this.initSlot("more", $moreSlot);
        }

        // Re-apply current state to the new DOM
        this.syncHiddenField();
        this.renderAllSlots();
        this.updateAllLabels();
    }

    // =====================================================================
    // Slot initialization — builds the full UI into a container
    // =====================================================================

    private initSlot(slotId: string, $container: JQuery) {
        // If this is the "more" slot, inject the full HTML structure
        // The "primary" slot already has the HTML from SearchForm.java
        if (slotId !== "primary") {
            $container.html(this.buildSlotHTML(slotId));
        }

        // For primary slot, add data attributes to existing elements for disambiguation
        if (slotId === "primary") {
            this.tagExistingElements(slotId);
        }

        const prefix = slotId === "primary" ? "" : slotId + "_";
        const slot: SlotElements = {
            slotId,
            $container,
            $pillbox: this.findInSlot($container, slotId, "zidxLocationPillbox"),
            $input: this.findInSlot($container, slotId, "zidxLocationInput"),
            $tagResults: this.findInSlot($container, slotId, "zidxLocationTagResults"),
            $addrInput: this.findInSlot($container, slotId, "zidxLocationAddressInput"),
            $addrSuggestions: this.findInSlot($container, slotId, "zidxLocationAddressSuggestions"),
            $radiusSelect: this.findInSlot($container, slotId, "zidxLocationRadiusSelect"),
            $featuredCities: this.findInSlot($container, slotId, "zidxLocationFeaturedCities"),
            $tabLocation: $container.find('.zidxLocationTab[data-tab="location"]'),
            $tabNearLocation: $container.find('.zidxLocationTab[data-tab="nearLocation"]'),
            $panelLocation: $container.find(".zidxLocationPanelLocation"),
            $panelNearLocation: $container.find(".zidxLocationPanelNearLocation"),
        };

        this.slots.push(slot);
        this.setupSlotEvents(slot);
        this.loadFeaturedCitiesInSlot(slot);
        this.renderSlot(slot);
    }

    /**
     * If the Near Address tab is active and the user typed an address but
     * never explicitly picked a suggestion, auto-select the first visible
     * suggestion so the search isn't silently empty.
     *
     * Returns a Promise that resolves once the place details have been
     * fetched (or immediately if there's nothing to do).
     */
    async commitPendingAddress(): Promise<void> {
        if (this.activeTab !== "nearLocation") return;
        if (this.selectedPlace) return;

        // Find the slot that has a non-empty address input
        let activeSlot: SlotElements | null = null;
        let inputVal = "";
        for (const slot of this.slots) {
            const val = (slot.$addrInput.val() as string || "").trim();
            if (val.length >= 3) {
                activeSlot = slot;
                inputVal = val;
                break;
            }
        }
        if (!activeSlot || inputVal.length < 3) return;

        // If there are visible suggestions, click the first/highlighted one
        const $items = activeSlot.$addrSuggestions.find(".zidxLocAddrItem");
        if ($items.length > 0) {
            const $active = activeSlot.$addrSuggestions.find(".zidxLocAddrActive");
            const $target = $active.length ? $active : $items.first();
            const placeId = $target.attr("data-place-id")!;
            const text = $target.text();

            // Update all slots' address inputs
            for (const slot of this.slots) {
                slot.$addrInput.val(text);
            }
            this.hideAddrSuggestions(activeSlot);
            await this.fetchPlaceDetails(placeId, text);
            return;
        }

        // No suggestions visible — force a fetch, then auto-select the first result
        if (!this.placesSessionId) {
            await this.startPlacesSession();
        }
        if (!this.placesSessionId) return;

        try {
            const resp = await fetch(
                "/places-autocomplete/?action=autocomplete" +
                "&sessionId=" + encodeURIComponent(this.placesSessionId) +
                "&input=" + encodeURIComponent(inputVal),
                { method: "POST" }
            );
            const data = await resp.json();
            const suggestions = data.suggestions || [];
            if (suggestions.length === 0) return;

            const first = suggestions[0].placePrediction;
            for (const slot of this.slots) {
                slot.$addrInput.val(first.text.text);
            }
            await this.fetchPlaceDetails(first.placeId, first.text.text);
        } catch (e) {
            console.error("commitPendingAddress fetch failed", e);
        }
    }

    public close() {
        // Hide suggestions in all slots
        for (const slot of this.slots) {
            this.hideAddrSuggestions(slot);
        }
        if (this.activeTab === "nearLocation") {
            this.commitPendingAddress().then(() => {
                console.log("selected first location");
            });
        }
    }

    /** For the primary slot, the elements already exist with fixed IDs. We locate them directly. */
    private tagExistingElements(slotId: string) {
        // No-op — primary elements use original IDs.
        // findInSlot handles the primary case by looking for the original IDs.
    }

    /** Find an element in a slot by its base ID. Primary uses original IDs, others use prefixed IDs. */
    private findInSlot($container: JQuery, slotId: string, baseId: string): JQuery {
        if (slotId === "primary") {
            return $("#" + baseId, this.root);
        }
        return $("#" + slotId + "_" + baseId, this.root);
    }

    /** Build the full location UI HTML for a non-primary slot. */
    private buildSlotHTML(slotId: string): string {
        const p = slotId + "_"; // prefix for IDs
        return '<div class="zidxSearchPopupDivElement">' +

            // Tabs
            '<div class="zidxLocationTabs" style="display:flex; gap:0; border-bottom:2px solid var(--border-color); margin-bottom:10px;">' +
            '<div class="zidxLocationTab zidxActive" data-tab="location">Location</div>' +
            '<div class="zidxLocationTab" data-tab="nearLocation">Near Address</div>' +
            '</div>' +

            // Location panel
            '<div class="zidxLocationPanel zidxLocationPanelLocation" style="display:block;">' +
            '<p style="font-size:13px; margin-bottom:10px; color:var(--text-color);">Type to search by city, ZIP, address, subdivision or county.</p>' +
            '<div class="zidxLocationPillbox" id="' + p + 'zidxLocationPillbox" ' +
            'style="display:flex; flex-wrap:wrap; gap:4px; align-items:center; ' +
            'border:1px solid #ccc; border-radius:4px; padding:4px 6px; min-height:34px; cursor:text;">' +
            '<input type="text" id="' + p + 'zidxLocationInput" class="zidxLocationPillboxInput" ' +
            'placeholder="Enter city, ZIP code, address, subdivision or county" autocomplete="new-password" ' +
            'aria-label="Search by location" ' +
            'style="border:none; outline:none; flex:1; min-width:120px; padding:2px 0; font-size:14px;">' +
            '</div>' +
            '<div id="' + p + 'zidxLocationTagResults"></div>' +
            '<div style="margin-top:10px; margin-bottom:10px; width:100%; float:left;"><a href="#" class="zidxSearchPopupApplyLink">Apply</a></div>' +
            '<div id="' + p + 'zidxLocationFeaturedCities" style="clear:both;"></div>' +
            '<div style="margin-top:10px;"><a href="#" class="zidxSearchPopupApplyLink">Apply</a></div>' +
            '</div>' +

            // Near Address panel
            '<div class="zidxLocationPanel zidxLocationPanelNearLocation" style="line-height:2; display:none;">' +
            '<p style="font-size:13px; margin:0 0 6px; color:var(--text-color);">Find listings near an address.</p>' +
            '<div id="' + p + 'zidxLocationAddressContainer" style="position:relative;">' +
            '<input type="text" id="' + p + 'zidxLocationAddressInput" ' +
            'placeholder="Enter an address..." autocomplete="new-password" ' +
            'aria-label="Search by address" ' +
            'style="width:100%; margin:0; padding:8px; box-sizing:border-box; border:1px solid var(--border-color); border-radius:4px; font-size:14px;">' +
            '<ul id="' + p + 'zidxLocationAddressSuggestions" ' +
            'style="display:none; list-style:none; padding:0; margin:4px 0 0; ' +
            'border:1px solid var(--border-color); background:var(--background-color); border-radius:4px; ' +
            'overflow:auto; z-index:100; width:100%; box-sizing:border-box;"></ul>' +
            '</div>' +
            '<div style="margin-top:10px;">' +
            '<label for="' + p + 'zidxLocationRadiusSelect" style="font-size:13px;">Within</label> ' +
            '<select id="' + p + 'zidxLocationRadiusSelect" style="padding:4px 8px; border-radius:4px; margin-left:0;">' +
            '<option value="1">1 mile</option>' +
            '<option value="3">3 miles</option>' +
            '<option value="5" selected>5 miles</option>' +
            '<option value="10">10 miles</option>' +
            '<option value="25">25 miles</option>' +
            '<option value="50">50 miles</option>' +
            '</select>' +
            '</div>' +
            '<div style="margin-top:10px;"><a href="#" class="zidxSearchPopupApplyLink">Apply</a></div>' +
            '</div>' +

            '</div>';
    }

    // =====================================================================
    // Per-slot event wiring
    // =====================================================================

    private setupSlotEvents(slot: SlotElements) {
        const self = this;

        // ── Tabs ──
        slot.$container.find(".zidxLocationTab").on("click", function (this: HTMLElement, e: JQuery.ClickEvent) {
            e.preventDefault();
            self.switchTab($(this).attr("data-tab")!);
        });

        // ── Pillbox: remove pill ──
        slot.$pillbox.on("click", ".zidxLocationPillClose", function (e: JQuery.ClickEvent) {
            e.preventDefault();
            e.stopPropagation();
            const idx = parseInt($(this).attr("data-index")!);
            self.locations.splice(idx, 1);
            self.renderAllSlots();
            self.syncHiddenField();
            self.triggerFormChange();
            slot.$input.trigger("focus");
        });

        // ── Pillbox: click to focus ──
        slot.$pillbox.on("click", function (e: JQuery.ClickEvent) {
            if (!$(e.target).closest(".zidxLocationPillClose").length) {
                slot.$input.trigger("focus");
            }
        });

        // ── Pillbox input: keyboard ──
        slot.$input.on("keydown", function (this: HTMLElement, e: JQuery.KeyDownEvent) {
            if (e.key === "Backspace" && (this as HTMLInputElement).value === "" && self.locations.length > 0) {
                self.locations.pop();
                self.renderAllSlots();
                self.syncHiddenField();
                self.triggerFormChange();
                return;
            }

            const $results = slot.$tagResults;
            const $items = $results.find(".zidxLocationTagButton");
            if ($items.length === 0) return;

            const $active = $results.find(".zidxLocationTagActive");
            const idx = $items.index($active);

            if (e.key === "ArrowDown") {
                e.preventDefault();
                $items.removeClass("zidxLocationTagActive");
                const next = idx < $items.length - 1 ? idx + 1 : 0;
                $items.eq(next).addClass("zidxLocationTagActive");
                self.scrollIntoView($items.eq(next)[0], $results.find("ul")[0]);
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                $items.removeClass("zidxLocationTagActive");
                const prev = idx <= 0 ? $items.length - 1 : idx - 1;
                $items.eq(prev).addClass("zidxLocationTagActive");
                self.scrollIntoView($items.eq(prev)[0], $results.find("ul")[0]);
            } else if (e.key === "Enter") {
                if ($active.length) {
                    e.preventDefault();
                    $active.trigger("click");
                }
            }
        });

        // ── Search tags autocomplete ──
        slot.$input.on("input", function (this: HTMLInputElement) {
            const val = this.value.trim();
            if (val.length < 3) {
                slot.$tagResults.empty();
                return;
            }
            if (self.currentTagsController) self.currentTagsController.abort();
            self.currentTagsController = new AbortController();

            fetch(self.zidx.options.selectedSiteDomain +"/search-tags?qs=" + encodeURIComponent(val), {
                method: "GET",
                signal: self.currentTagsController.signal
            })
                .then(r => r.text())
                .then(data => {
                    self.renderSearchTagResults(slot, data);
                    self.currentTagsController = null;
                })
                .catch(err => {
                    if (err.name !== "AbortError") console.error("search-tags error", err);
                    self.currentTagsController = null;
                });
        });

        slot.$input.on("focus", function (this: HTMLInputElement) {
            if (this.value.trim().length >= 3) {
                $(this).trigger("input");
            }
        });

        // ── Address autocomplete ──
        slot.$addrInput.on("focus", function () {
            if (!self.placesSessionId) self.startPlacesSession();
        });

        slot.$addrInput.on("input", function (this: HTMLInputElement) {
            const val = this.value.trim();
            if (self.selectedPlace && val !== self.selectedPlace.formattedAddress) {
                self.selectedPlace = null;
                self.clearNearLocationFields();
                self.updateAllLabels();
                self.triggerFormChange();
            }
            clearTimeout(self.addrSuggestionTimeout);
            self.addrSuggestionTimeout = setTimeout(() => self.fetchAddrSuggestions(slot, val), 300);
        });

        slot.$addrInput.on("keydown", function (e: JQuery.KeyDownEvent) {
            if (e.key === "Escape") {
                if (self.addressSuggestionsVisible) {
                    e.preventDefault();
                    e.stopPropagation();
                    self.hideAddrSuggestions(slot);
                }
                return;
            }
            if (!self.addressSuggestionsVisible) return;

            const $items = slot.$addrSuggestions.find(".zidxLocAddrItem");
            if ($items.length === 0) return;
            const $active = slot.$addrSuggestions.find(".zidxLocAddrActive");
            const idx = $items.index($active);

            if (e.key === "ArrowDown") {
                e.preventDefault(); e.stopPropagation();
                $items.removeClass("zidxLocAddrActive");
                const next = idx < $items.length - 1 ? idx + 1 : 0;
                $items.eq(next).addClass("zidxLocAddrActive");
                self.scrollIntoView($items.eq(next)[0], slot.$addrSuggestions[0]);
            } else if (e.key === "ArrowUp") {
                e.preventDefault(); e.stopPropagation();
                $items.removeClass("zidxLocAddrActive");
                const prev = idx <= 0 ? $items.length - 1 : idx - 1;
                $items.eq(prev).addClass("zidxLocAddrActive");
                self.scrollIntoView($items.eq(prev)[0], slot.$addrSuggestions[0]);
            } else if (e.key === "Enter") {
                e.preventDefault(); e.stopPropagation();
                if ($active.length) {
                    $active.trigger("click");
                } else if ($items.length === 1) {
                    $items.first().trigger("click");
                }
                slot.$addrInput.trigger("blur");
            } else if (e.key === "Tab") {
                self.hideAddrSuggestions(slot);
            }
        });

        // Close address suggestions on outside click
        $(document).on("click", function (e: JQuery.ClickEvent) {
            if (!$(e.target).closest(slot.$addrSuggestions.parent()).length &&
                !$(e.target).is(slot.$addrInput)) {
                self.hideAddrSuggestions(slot);
            }
        });

        // ── Radius select ──
        slot.$radiusSelect.on("change", function () {
            if (self.selectedPlace) {
                self.applyNearLocation();
            }
        });
    }

    // =====================================================================
    // Rendering — updates ALL slots from shared state
    // =====================================================================

    private renderAllSlots() {
        for (const slot of this.slots) {
            this.renderSlot(slot);
        }
    }

    private renderSlot(slot: SlotElements) {
        this.renderPillsInSlot(slot);
        this.renderTabState(slot);
        this.updateSlotLabel(slot);

        // Sync address input and radius across slots
        if (this.selectedPlace) {
            slot.$addrInput.val(this.selectedPlace.formattedAddress);
        } else {
            // Only clear if user hasn't typed something new in THIS slot
            // (avoid clearing mid-typing in another slot)
        }

        if (this.activeTab === "nearLocation" && this.selectedPlace) {
            const r = $("#zidx_nearlocation_distanceInMiles", this.root).val() || "5";
            slot.$radiusSelect.val(String(r));
        }
    }

    private renderPillsInSlot(slot: SlotElements) {
        slot.$pillbox.find(".zidxLocationPill").remove();

        for (let i = 0; i < this.locations.length; i++) {
            const loc = this.locations[i];
            let label = loc.field === "postalCode" ? "ZIP Code" : loc.field;

            const $pill = $('<span class="zidxLocationPill"></span>')
                .attr("data-index", i)
                .css({
                    display: "inline-flex", alignItems: "center", gap: "4px",
                    borderRadius: "5px", padding: "5px 10px",
                    fontSize: "14px", whiteSpace: "nowrap"
                });

            $pill.append($('<span class="zidxLocationPillLabel"></span>')
                .text(label + ": " + loc.value));

            $pill.append($('<button type="button" class="zidxLocationPillClose"></button>')
                .attr({ "data-index": i, "aria-label": "Remove " + loc.value })
                .css({ border: "none", background: "none", cursor: "pointer", fontWeight: "bold", padding: "0 2px" })
                .html("&times;"));

            slot.$input.before($pill);
        }
    }

    private renderTabState(slot: SlotElements) {
        const tab = this.activeTab;

        slot.$container.find(".zidxLocationTab").each(function (this: HTMLElement) {
            const isActive = $(this).attr("data-tab") === tab;
            $(this).toggleClass("zidxActive", isActive)
                .css("font-weight", isActive ? "600" : "400")
                .css("background-color", isActive ? "rgba(128,128,128,.3)" : "transparent")
                .css("border-bottom-color", isActive ? "#333" : "transparent");
        });

        slot.$panelLocation.toggle(tab === "location");
        slot.$panelNearLocation.toggle(tab === "nearLocation");
    }

    private updateAllLabels() {
        for (const slot of this.slots) {
            this.updateSlotLabel(slot);
        }
        // Also update the top bar's label
        const $topLabel = $("#zidxSearchLocationContainer .zidxSearchLabel", this.root);
        const $moreValues = $("#zidxMoreLocationValues", this.root);

        let summary = "";
        if (this.activeTab === "nearLocation" && this.selectedPlace) {
            const r = parseFloat(($("#zidx_nearlocation_distanceInMiles", this.root).val() || "5") as string);
            summary = "Within " + r + " mi of " + this.selectedPlace.formattedAddress;
        } else if (this.locations.length > 0) {
            summary = this.locations.map(l => l.value).join(", ");
        }

        if ($topLabel.length) {
            $topLabel.text(summary ? this.truncate(summary, 20) : "Location");
        }
        if ($moreValues.length) {
            $moreValues.text(summary);
        }
    }

    private updateSlotLabel(slot: SlotElements) {
        // Individual slot doesn't need its own label — updateAllLabels covers the shared labels
    }

    // =====================================================================
    // Shared actions — update state, then re-render all slots
    // =====================================================================

    public switchTab(tab: string) {
        this.activeTab = tab;
        $("#zidx_search_location_mode", this.root).val(tab);
        this.renderAllSlots();
        this.updateAllLabels();
        this.triggerFormChange();
    }

    addLocation(field: string, value: string) {
        for (const loc of this.locations) {
            if (loc.field === field && loc.value === value) return;
        }
        this.locations.push({ field, value });
        // Clear all search inputs and tag results across slots
        for (const slot of this.slots) {
            slot.$input.val("");
            slot.$tagResults.empty();
        }
        this.renderAllSlots();
        this.syncHiddenField();
        this.triggerFormChange();
    }

    // =====================================================================
    // Search-tags autocomplete (renders into the requesting slot only)
    // =====================================================================

    private renderSearchTagResults(slot: SlotElements, response: string) {
        slot.$tagResults.empty();

        const lines = response.trim().split("\n");
        if (lines.length < 2) return;
        const dataLines = lines.slice(1);
        if (dataLines.length === 0) return;

        const self = this;
        const $list = $("<ul></ul>").css({
            listStyle: "none", padding: "0", margin: "5px 0",
            border: "1px solid var(--border-color)", borderRadius: "5px", maxHeight: "200px", overflow: "auto"
        });

        for (const line of dataLines) {
            const parts = line.split("\t");
            if (parts.length < 4) continue;

            const field = parts[0];
            const value = parts[1];
            const count = parts[2];
            const fieldLabel = field === "postalCode" ? "ZIP Code" : field;

            const $btn = $('<button type="button" class="zidxLocationTagButton"></button>')
                .attr({ "data-field": field, "data-value": value })
                .html(
                    '<span style="font-size:12px;color:var(--text-color);min-width:70px;">' + this.esc(fieldLabel) + '</span>' +
                    '<span style="color:var(--text-color);">' + this.esc(value) + ' (' + this.esc(count) + ')</span>'
                );

            $btn.on("click", function (e: JQuery.ClickEvent) {
                e.preventDefault();
                self.addLocation($(this).attr("data-field")!, $(this).attr("data-value")!);
                slot.$input.trigger("focus");
            });
            $btn.on("mouseover", function () {
                $list.find(".zidxLocationTagActive").removeClass("zidxLocationTagActive");
                $(this).addClass("zidxLocationTagActive");
            });

            $list.append($("<li></li>").css({ padding: 0, margin: 0 }).append($btn));
        }

        slot.$tagResults.append($list);
    }

    // =====================================================================
    // Address autocomplete (renders into the requesting slot only)
    // =====================================================================

    private async startPlacesSession() {
        try {
            const resp = await fetch("/places-autocomplete/?action=newSession", { method: "POST" });
            const data = await resp.json();
            this.placesSessionId = data.sessionId;
        } catch (e) {
            console.error("Failed to start places session", e);
        }
    }

    private async fetchAddrSuggestions(slot: SlotElements, input: string) {
        if (!this.placesSessionId || input.length < 3) {
            this.hideAddrSuggestions(slot);
            return;
        }
        try {
            const resp = await fetch(
                "/places-autocomplete/?action=autocomplete" +
                "&sessionId=" + encodeURIComponent(this.placesSessionId) +
                "&input=" + encodeURIComponent(input),
                { method: "POST" }
            );
            const data = await resp.json();

            if (data.rateLimited) {
                clearTimeout(this.addrSuggestionTimeout);
                this.addrSuggestionTimeout = setTimeout(() => this.fetchAddrSuggestions(slot, input), 300);
                return;
            }
            if (data.expired) {
                await this.startPlacesSession();
                clearTimeout(this.addrSuggestionTimeout);
                this.addrSuggestionTimeout = setTimeout(() => this.fetchAddrSuggestions(slot, input), 300);
                return;
            }
            this.renderAddrSuggestions(slot, data.suggestions || []);
        } catch (e) {
            console.error("Autocomplete fetch failed", e);
        }
    }

    private renderAddrSuggestions(slot: SlotElements, suggestions: any[]) {
        if (!suggestions.length) { this.hideAddrSuggestions(slot); return; }

        this.addressSuggestionsVisible = true;
        slot.$addrSuggestions.empty().show();
        const self = this;

        for (const s of suggestions) {
            const prediction = s.placePrediction;
            const placeId = prediction.placeId;
            const text = prediction.text.text;

            const $li = $('<li class="zidxLocAddrItem"></li>')
                .text(text)
                .css({ padding: "5px 10px", cursor: "pointer", borderBottom: "1px solid var(--border-color)" })
                .attr("data-place-id", placeId);

            $li.on("click", function (e: JQuery.ClickEvent) {
                e.preventDefault(); e.stopPropagation();
                self.hideAddrSuggestions(slot);
                self.fetchPlaceDetails(placeId, text);
            });
            $li.on("mouseover", function () {
                slot.$addrSuggestions.find(".zidxLocAddrActive").removeClass("zidxLocAddrActive");
                $(this).addClass("zidxLocAddrActive");
            });
            slot.$addrSuggestions.append($li);
        }
    }

    private hideAddrSuggestions(slot: SlotElements) {
        this.addressSuggestionsVisible = false;
        slot.$addrSuggestions.empty().hide();
    }

    private async fetchPlaceDetails(placeId: string, displayText: string) {
        try {
            const resp = await fetch(
                "/places-autocomplete/?action=placeDetails" +
                "&sessionId=" + encodeURIComponent(this.placesSessionId || "") +
                "&placeId=" + encodeURIComponent(placeId),
                { method: "POST" }
            );
            const data = await resp.json();

            this.selectedPlace = {
                formattedAddress: data.formattedAddress,
                lat: data.location.latitude,
                lng: data.location.longitude
            };

            // Update address input in ALL slots
            for (const slot of this.slots) {
                slot.$addrInput.val(data.formattedAddress || displayText);
            }

            this.placesSessionId = null;
            this.applyNearLocation();
        } catch (e) {
            console.error("Place details fetch failed", e);
        }
    }

    public applyNearLocation() {
        if (!this.selectedPlace) return;

        // Read radius from whichever slot has it set (they should be in sync)
        let radius = 5;
        for (const slot of this.slots) {
            const val = parseInt((slot.$radiusSelect.val() || "5") as string);
            if (val > 0) { radius = val; break; }
        }

        // Sync radius across all slots
        for (const slot of this.slots) {
            slot.$radiusSelect.val(String(radius));
        }

        $("#zidx_nearlocation_address", this.root).val(this.selectedPlace.formattedAddress);
        $("#zidx_nearlocation_latitude", this.root).val(this.selectedPlace.lat);
        $("#zidx_nearlocation_longitude", this.root).val(this.selectedPlace.lng);
        $("#zidx_nearlocation_distanceInMiles", this.root).val(radius);

        this.updateAllLabels();
        this.triggerFormChange();
    }

    // =====================================================================
    // Featured cities
    // =====================================================================


    private loadFeaturedCitiesInSlot(slot: SlotElements) {
        const self = this;
        const cities = this.featuredCities;
        if (cities.length === 0) return;

        cities.sort();

        // 1. Inject a small style block to easily toggle the hidden cities without breaking grid item flow
        let html = '<style>' +
            '.zidx-hidden-city { display: none !important; }' +
            '</style>';

        let limit=20;
        html += '<p style="font-size:14px; margin:6px 0 4px; color:#666;">Featured Cities</p>' +
            '<div class="zidxCitiesGrid" style="display:grid; grid-template-columns:repeat(auto-fill, minmax(140px, 1fr)); gap:4px;">';

        // 2. Loop through all cities in a single container
        cities.forEach((c, index) => {
            const hiddenClass = index >= limit ? ' zidx-hidden-city' : '';

            html += '<a href="#" class="zidxLocationCityLink zidxDisableSPALink' + hiddenClass + '" data-city="' + this.esc(c) + '" ' +
                'style="padding:5px 5px; font-size:14px; text-decoration:none; color:var(--text-color); ' +
                'background:none; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">' + this.esc(c) + '</a>';
        });

        html += '</div>';

        // 3. Append the toggle button if there are more than limit cities
        if (cities.length > limit) {
            const remainingCount = cities.length - limit;
            html += '<a href="#" class="zidxToggleCitiesBtn" style="display:inline-block; margin-top:6px; font-size:13px; color:#0066cc; text-decoration:none; font-weight:500;">' +
                'Show more (' + remainingCount + ')' +
                '</a>';
        }

        slot.$featuredCities.html(html);

        // City click handler
        slot.$featuredCities.off("click", ".zidxLocationCityLink").on("click", ".zidxLocationCityLink", function (e: JQuery.ClickEvent) {
            e.preventDefault();
            self.addLocation("City", $(this).attr("data-city")!);
        });

        // Toggle handler acting on items inside the single container
        slot.$featuredCities.off("click", ".zidxToggleCitiesBtn").on("click", ".zidxToggleCitiesBtn", function (e: JQuery.ClickEvent) {
            e.preventDefault();
            const $btn = $(this);
            const $grid = slot.$featuredCities.find(".zidxCitiesGrid");
            const $hiddenCities = $grid.find(".zidxLocationCityLink").slice(limit);
            const remainingCount = cities.length - limit;

            // Check if the 13th city is currently hidden
            if ($hiddenCities.first().hasClass("zidx-hidden-city")) {
                $hiddenCities.removeClass("zidx-hidden-city");
                $btn.text('Show less');
            } else {
                $hiddenCities.addClass("zidx-hidden-city");
                $btn.text('Show more (' + remainingCount + ')');
            }
        });
    }

    // =====================================================================
    // Public API — called by base.ts / buildJsonSearch
    // =====================================================================

    getLocationQuery(): string {
        if (this.activeTab !== "location" || this.locations.length === 0) return "";

        const byField: Record<string, string[]> = {};
        for (const loc of this.locations) {
            if (!byField[loc.field]) byField[loc.field] = [];
            byField[loc.field].push("'" + loc.value.replace(/'/g, "\\'") + "'");
        }

        const clauses: string[] = [];
        for (const field in byField) {
            clauses.push(field + " IN (" + byField[field].join(",") + ")");
        }
        return clauses.length === 1 ? clauses[0] : "(" + clauses.join(" OR ") + ")";
    }

    isNearLocationActive(): boolean {
        return this.activeTab === "nearLocation" && this.selectedPlace !== null;
    }

    restoreLocations(tags: LocationTag[]) {
        this.locations = tags;
        this.renderAllSlots();
        this.syncHiddenField();
    }

    restoreNearLocation(address: string, lat: number, lng: number, distMiles: number) {
        if (!address) return;
        this.selectedPlace = { formattedAddress: address, lat, lng };
        for (const slot of this.slots) {
            slot.$addrInput.val(address);
            slot.$radiusSelect.val(String(distMiles));
        }
        this.switchTab("nearLocation");
        this.applyNearLocation();
    }

    clear() {
        this.locations = [];
        this.selectedPlace = null;
        this.activeTab = "location";
        this.clearNearLocationFields();
        for (const slot of this.slots) {
            slot.$input.val("");
            slot.$addrInput.val("");
            slot.$tagResults.empty();
            this.hideAddrSuggestions(slot);
        }
        this.syncHiddenField();
        this.renderAllSlots();
        this.updateAllLabels();
    }

    // =====================================================================
    // Internal helpers
    // =====================================================================

    private syncHiddenField() {
        let values=[];
        for(let i=0;i<this.locations.length;i++){
            values.push(this.locations[i].field + ": " + this.locations[i].value);
        }
        // console.log(this.locations);
        $("#zidx_search_location_tags", this.root).val(values.join(","));

        // const cities = this.locations.filter(l => l.field === "City").map(l => l.value);
        // $("input[name='zidx_search_city'], input[name='zidx_more_search_city']", this.root).each(function (this: HTMLElement) {
        //     if (this instanceof HTMLInputElement) {
        //         this.checked = cities.includes(this.value);
        //     }
        // });
    }

    private clearNearLocationFields() {
        $("#zidx_nearlocation_address", this.root).val("");
        $("#zidx_nearlocation_latitude", this.root).val("0");
        $("#zidx_nearlocation_longitude", this.root).val("0");
        $("#zidx_nearlocation_distanceInMiles", this.root).val("0");
    }

    private triggerFormChange() {
        $("#zidx_search_location_tags", this.root).trigger("change");
    }

    private scrollIntoView(el: HTMLElement, container: HTMLElement) {
        if (!el || !container) return;
        const top = el.offsetTop;
        const bottom = top + el.offsetHeight;
        if (top < container.scrollTop) {
            container.scrollTop = top;
        } else if (bottom > container.scrollTop + container.clientHeight) {
            container.scrollTop = bottom - container.clientHeight;
        }
    }

    private esc(s: string): string {
        return s.replace(/&/g, "&amp;").replace(/</g, "&lt;")
            .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    }

    private truncate(s: string, max: number): string {
        return s.length > max ? s.substring(0, max) + "\u2026" : s;
    }
}